import type { EntityData } from '@adornis/baseql/entities/types.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { IBANInput } from '@adornis/formfield/fields/iban-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { CountryPickerInput } from '@adornis/internationalization/client/language-picker/CountryPickerInput.js';
import { iban } from '@adornis/validation/functions/iban.js';
import type { ValidationFunction, ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import type { Person } from '../../entities/person.js';
import type { UserOnUserPayload } from '../../UserPolicy.js';
import type { BankDaten } from '../entities/BankDaten.js';
import { UserProfileActions } from '../UserProfilePolicy.js';

const LegacyIbanField = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const legacyIbans = props.form.document.profilDaten?.bankInfos?.legacyIbans ?? [];

  const updateLegacyIbans = (nextLegacyIbans: string[]) => {
    const bankInfos = props.form.document.profilDaten?.bankInfos;
    if (!bankInfos) return;
    bankInfos.legacyIbans = nextLegacyIbans;
  };

  const addLegacyIban = () => {
    updateLegacyIbans([...legacyIbans, '']);
  };

  const removeLegacyIban = (index: number) => {
    updateLegacyIbans(legacyIbans.filter((_, currentIndex) => currentIndex !== index));
  };

  return html`
    <div class="flex flex-col gap-3 pl-4 border-l-2 border-base-300">
      ${
        legacyIbans.length
          ? repeat(
              legacyIbans,
              (legacyIban, index) => `${legacyIban}-${index}`,
              (_, index) => html`
                <div class="flex flex-row items-center gap-2">
                  <div class="flex-1">
                    ${IBANInput({
                      ...props.form.field('profilDaten', 'bankInfos', 'legacyIbans', index),
                      placeholder: 'Keine alte IBAN hinterlegt',
                      placeholderMode: 'hide',
                    })}
                  </div>
                  <button class="btn btn-ghost" type="button" @click=${() => removeLegacyIban(index)}>
                    <span class="icon-[material-symbols--delete] text-lg"></span>
                  </button>
                </div>
              `,
            )
          : html`<p class="text-sm text-base-content/70">Keine alte IBAN hinterlegt</p>`
      }

      <button class="btn btn-dash btn-block justify-start" type="button" @click=${addLegacyIban}>
        <span class="icon-[material-symbols--add] text-success"></span>
        Alte IBAN hinzufügen
      </button>
    </div>
  `;
});

export const BankDatenFormSepa = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const { can: canEditLegacyIbans } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_LEGACY_IBANS,
    viewedUser: props.form.document,
  });

  return html`
    ${FormGridEntry({
      label: 'IBAN',
      render: IBANInput({
        ...props.form.field('profilDaten', 'bankInfos', 'iban'),
        placeholder: 'Keine IBAN hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${
      canEditLegacyIbans
        ? FormGridEntry({
            label: 'Alte IBANs',
            render: LegacyIbanField({ form: props.form }),
            labelTopMargin: 'mt-4',
            labelSubtext:
              'Diese IBANs werden nicht mehr verwendet, aber ermöglichen der Person, alte Quittungen einzusehen.',
          })
        : nothing
    }
    ${FormGridEntry({
      label: 'Kontoinhaber',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'kontoinhaber'),
        placeholder: 'Kein Kontoinhaber hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
  `;
});

export const BankDatenFormNotSepa = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  return html`
    ${FormGridEntry({
      label: 'Land',
      render: CountryPickerInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaLand'),
        placeholder: 'Keine Land hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
    ${FormGridEntry({
      label: 'Kreditinstitut',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaKreditinstitut'),
        placeholder: 'Kein Kreditinstitut hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
    ${FormGridEntry({
      label: 'Ort',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaOrt'),
        placeholder: 'Kein Ort hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
    ${FormGridEntry({
      label: 'SWIFT-Code',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaSWIFT'),
        placeholder: 'Kein SWIFT-Code hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
    ${FormGridEntry({
      label: 'IBAN',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaIBAN'),
        placeholder: 'Keine IBAN hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
    ${FormGridEntry({
      label: 'Länderschlüssel',
      render: TextInput({
        ...props.form.field('profilDaten', 'bankInfos', 'nichtSepaLaenderschluessel'),
        placeholder: 'Kein Länderschlüssel hinterlegt',
        placeholderMode: 'hide',
      }),
    })}
  `;
});

export const BankDatenForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Kontoart',
        render: DropdownSelectionInput({
          ...props.form.field('profilDaten', 'bankInfos', 'nichtSepa'),
          selectables: [false, true],
          renderString: value => (value ? 'Nicht-SEPA' : 'SEPA'),
          searchable: false,
        }),
      })}
      ${
        props.form.document.profilDaten?.bankInfos?.nichtSepa
          ? BankDatenFormNotSepa({ form: props.form })
          : BankDatenFormSepa({ form: props.form })
      }
    </div>
  `;
});

const ifSepaIs =
  (sepaTrue: boolean) => (fn: ValidationFunction<any, BankDaten>) => (v: ValidationFunctionOptions<any, BankDaten>) => {
    return v.target.nichtSepa === sepaTrue ? fn(v) : true;
  };

const ifSEPA = ifSepaIs(false);
const ifNotSEPA = ifSepaIs(true);

export const BankDatenFormValidator = {
  iban: ifSEPA(iban({ allowEmpty: true })),
  nichtSepaLand: ifNotSEPA(sgNonOptional()),
  nichtSepaKreditinstitut: ifNotSEPA(sgNonOptional()),
  nichtSepaOrt: ifNotSEPA(sgNonOptional()),
  nichtSepaSWIFT: ifNotSEPA(sgNonOptional()),
  nichtSepaIBAN: ifNotSEPA(sgNonOptional()),
  nichtSepaLaenderschluessel: ifNotSEPA(sgNonOptional()),
};
